import { createAction } from 'redux-actions';
import { forEach, groupBy, lowerCase, mapKeys, upperCase } from 'lodash';

import {
  BUY,
  DIST_TO_SCHOOL,
  EXCHANGE,
  PURCHASE_NEW,
  SELL,
  TransactionTypes,
} from '../../../settings/settings';
import { callApi, getEndpoint } from '../../../utils/api';

export const buyLabel = lowerCase(BUY);
export const exchangeLabel = lowerCase(EXCHANGE);
export const rothConversionLabel = 'roth conversion';
export const sellLabel = lowerCase(SELL);

export const PENDING_TRANSACTIONS = 'PENDING_TRANSACTIONS';
export const PENDING_TRANSACTIONS_PENDING = PENDING_TRANSACTIONS + '_PENDING';
export const PENDING_TRANSACTIONS_FULFILLED =
  PENDING_TRANSACTIONS + '_FULFILLED';
export const PENDING_TRANSACTIONS_REJECTED = PENDING_TRANSACTIONS + '_REJECTED';

const groupTransactionsByType = (transactions) => {
  if (transactions && transactions[0] === null) {
    return;
  }
  const temp = mapKeys(
    groupBy(transactions, 'type.enumerationName'),
    (_value, key) => {
      switch (key) {
        case `${TransactionTypes[BUY]}`:
          return buyLabel;
        case `${TransactionTypes[SELL]}`:
        case `${TransactionTypes[DIST_TO_SCHOOL]}`:
          return sellLabel;
        case `${TransactionTypes[EXCHANGE]}`:
          return exchangeLabel;
        case 'Roth Conversion':
          return rothConversionLabel;
        default:
          return buyLabel;
      }
    }
  );
  let pendingExchangeArray;
  if (
    temp[rothConversionLabel] !== undefined &&
    temp[exchangeLabel] !== undefined
  ) {
    pendingExchangeArray = temp[exchangeLabel].concat(
      temp[rothConversionLabel]
    );
  } else if (
    temp[rothConversionLabel] === undefined &&
    temp[exchangeLabel] !== undefined
  ) {
    pendingExchangeArray = temp[exchangeLabel];
  } else if (
    temp[rothConversionLabel] !== undefined &&
    temp[exchangeLabel] === undefined
  ) {
    pendingExchangeArray = temp[rothConversionLabel];
  }
  return {
    //concatenates exchange and roth conversion pending transactions together
    buy: temp[buyLabel],
    sell: temp[sellLabel],
    exchange: pendingExchangeArray,
  };
};

function groupTransactionsByTypeAndAgreements(transactions) {
  const byTransactionType = {};

  const cacheTransactionRecord = (type, record, key) => {
    if (!key) {
      return;
    }

    byTransactionType[type] = {
      ...byTransactionType[type],
    };
    const transactionsThisAgreement =
      byTransactionType[type][key] || (byTransactionType[type][key] = []);
    transactionsThisAgreement.push(record);
  };

  const pivotTransactionRecordByAgreementId = (
    transactionType,
    record,
    description
  ) => {
    const agreementKey =
      upperCase(description) === PURCHASE_NEW
        ? record.targetPositionKey && record.targetPositionKey.agreementKey
        : record.sourcePositionKey && record.sourcePositionKey.agreementKey;

    if (!agreementKey) {
      return;
    }

    cacheTransactionRecord(transactionType, record, agreementKey.agreementID);
    cacheTransactionRecord(
      transactionType,
      record,
      agreementKey.agreementNumber
    );
    cacheTransactionRecord(
      transactionType,
      record,
      agreementKey.agreementNumber + '-' + agreementKey.checkSum
    );
  };

  forEach(
    groupTransactionsByType(transactions),
    (transaction, transactionType) => {
      byTransactionType[transactionType] = {};
      forEach(transaction, (thisTransaction) => {
        if (thisTransaction != null) {
          const { description, transactionRecords } = thisTransaction;
          forEach(transactionRecords, (transactionRecord) => {
            pivotTransactionRecordByAgreementId(
              transactionType,
              transactionRecord,
              description
            );
          });
        }
      });
    }
  );
  return byTransactionType;
}

export default function pendingTransactions(
  state = {
    isLoading: false,
    isLoaded: false,
    hasFailed: false,
    error: null,
    transactionListByType: {},
    recordsByTypeAndAgreement: {},
  },
  { payload, type }
) {
  switch (type) {
    case PENDING_TRANSACTIONS_PENDING:
      return {
        ...state,
        isLoading: true,
        isLoaded: false,
      };

    case PENDING_TRANSACTIONS_FULFILLED:
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        hasFailed: false,
        error: null,
        transactionListByType: groupTransactionsByType(payload.body),
        recordsByTypeAndAgreement: groupTransactionsByTypeAndAgreements(
          payload.body
        ),
      };

    case PENDING_TRANSACTIONS_REJECTED:
      return {
        ...state,
        isLoading: false,
        isLoaded: false,
        hasFailed: true,
        error: payload,
        transactionListByType: {},
        recordsByTypeAndAgreement: {},
      };

    default:
      return state;
  }
}

export function getPendingTransactions() {
  return function getPendingTransactionsImpl() {
    const actionCreator = createAction(PENDING_TRANSACTIONS);
    const apiPromise = callApi(`${getEndpoint('pendingTransactions')}`);
    const action = actionCreator(apiPromise);
    return action;
  };
}
